import { QueryClient, hashKey, type QueryKey } from '@tanstack/react-query';
import { getActiveTenantId } from '@/lib/active-tenant';

/**
 * Cache identity of a query = active tenant + the query key.
 *
 * WHY HERE AND NOT IN THE 48 KEY FACTORIES of `src/api/queries/`.
 * None of them carries a `tenantId`: `['clients','list',{}]` is the same
 * key for every tenant, so an entry cached by tenant A is a perfectly
 * valid cache hit for tenant B. Purging at logout only defends an ORDERLY
 * exit — a killed tab, a crashed browser or a power cut never runs it.
 *
 * Adding the tenant to each factory would be a convention, and a
 * convention that must be remembered is a convention that will be
 * forgotten by whoever adds the 49th file. `queryKeyHashFn` is not a
 * convention: react-query computes the cache identity of EVERY query
 * through it (`hashQueryKeyByOptions`, called from
 * `QueryClient.defaultQueryOptions` — hence `useQuery`, `getQueryData`,
 * `setQueryData`, `prefetchQuery`, `ensureQueryData` alike). A new query
 * is scoped whether its author knows this file exists or not.
 *
 * Filtering (`invalidateQueries`, `removeQueries`, `findAll`) matches on
 * the query key ARRAY, never on the hash — prefix invalidation used all
 * over the app keeps working unchanged.
 */
export function hashQueryKeyByTenant(queryKey: QueryKey): string {
  return hashKey([getActiveTenantId(), queryKey]);
}

/**
 * The application's React Query client. Extracted from `main.tsx` so the
 * cache-identity rule above can be tested for itself.
 */
export function createAppQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 1000 * 60 * 5, // 5 minutes
        retry: 1,
        refetchOnWindowFocus: false,
        queryKeyHashFn: hashQueryKeyByTenant,
      },
      // A write runs while the browser reports itself offline: the transport
      // queues it or refuses it, instead of React Query pausing it in memory.
      mutations: { networkMode: 'always' },
    },
  });
}
